import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import classnames from 'classnames';
import { times } from 'lodash-es';

// Models
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import {
  buildAuthorArticlesPath,
  buildAuthorVideosPath,
  EditorialEntities,
  EditorialModel,
} from 'client/data/models/editorial';

// Utils
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getAuthorContentPath } from 'site-modules/editorial/utils/content-path';

// Constants
import { ARTICLE_PAGE_SIZE } from 'site-modules/editorial/components/author-article-page/constants';

// Shared components
import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { TabsPanel } from 'site-modules/shared/components/tabs-panel/tabs-panel';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import {
  AuthorArticlesPage,
  AuthorVideosPage,
} from 'site-modules/editorial/components/author-article-page/author-article-page';

import './author.scss';

const TAB_NAMES = { profile: 'profile', articles: 'articles', videos: 'videos' };
const TABS_DATA = [
  { value: TAB_NAMES.profile, label: 'Profile', ariaControls: 'profile-tab' },
  { value: TAB_NAMES.articles, label: 'Articles', ariaControls: 'articles-tab' },
  { value: TAB_NAMES.videos, label: 'Videos', ariaControls: 'video-tab' },
];

const ShowMoreBtn = ({ onClick, ariaLabel }) => (
  <div className="text-center mt-3_5 mb-3_5">
    <Button onClick={onClick} color="black" className="size-16 p-0" aria-label={ariaLabel}>
      Show more
      <span className="d-block icon-arrow-down2 mt-0-5" aria-hidden />
    </Button>
  </div>
);

function AuthorUI({ content = null, articlesData = {}, videosData = {}, author }) {
  const [loadedArticlePagesNum, setLoadedArticlePagesNum] = useState(1);
  const [loadedVideoPagesNum, setLoadedVideoPagesNum] = useState(1);
  const [videoModalData, setVideoModalData] = useState({});
  const [isVideoModalOpen, toggleVideoModalOpen] = useToggle(false);
  const [selectedTab, setSelectedTab] = useState(TABS_DATA[0].value);

  const { totalPages: totalArticlesPages } = articlesData;
  const { totalPages: totalVideosPages } = videosData;

  const onToggleVideoModal = useCallback(newVideoModalData => {
    toggleVideoModalOpen();
    setVideoModalData(newVideoModalData || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNextArticlePage = useCallback(() => {
    setLoadedArticlePagesNum(pageNum => pageNum + 1);
  }, []);

  const loadNextVideoPage = useCallback(() => {
    setLoadedVideoPagesNum(pageNum => pageNum + 1);
  }, []);

  if (!content) {
    return null;
  }

  const authorContent = content.child('author');
  const {
    authorImage,
    authorTitle,
    authorBio,
    authorName,
    authorTwitter,
    authorYouTube,
    authorInstagram,
    authorLinkedIn,
    authorFacebook,
  } = authorContent.getAllMetadata();

  const socialLinks = [
    { icon: 'icon-instagram', iconTitle: 'Instagram', link: authorInstagram },
    { icon: 'icon-x', iconTitle: 'X', link: authorTwitter },
    { icon: 'icon-linkedin', iconTitle: 'LinkedIn', link: authorLinkedIn },
    { icon: 'icon-play', iconTitle: 'YouTube', link: authorYouTube },
    { icon: 'icon-facebook2', iconTitle: 'Facebook', link: authorFacebook },
  ].filter(({ link }) => link);

  const displayedTabs = TABS_DATA.filter(
    ({ value }) =>
      value === TAB_NAMES.profile ||
      (value === TAB_NAMES.articles && totalArticlesPages > 0) ||
      (value === TAB_NAMES.videos && totalVideosPages > 0)
  );

  return (
    <Container tag="main" id="main-content" tabIndex={-1} className="author">
      <Row className="align-items-start">
        <Col xs={12} md={4} className="sticky-content pt-2">
          <section className="author-photo pos-r mb-1 rounded-16 overflow-hidden">
            <img src={authorImage} alt="" className="w-100" />
            <hgroup className="author-info pos-a bottom-0 w-100 px-1 py-1">
              <h1 className="size-20 fw-medium text-white mb-0_25">{authorName}</h1>
              <ContentFragment componentToUse="p" classes="medium fw-normal text-blue-80 mb-0">
                {authorTitle}
              </ContentFragment>
            </hgroup>
          </section>
          {!!socialLinks.length && (
            <section className="contact-info px-1_5 py-1_5 rounded-12 pos-r">
              <h2 className="size-24 fw-bold text-cool-gray-10 mb-0_75">Contact</h2>
              <ul className="d-flex list-unstyled mb-0">
                {socialLinks.map(({ iconTitle, icon, link }) => (
                  <li key={icon} className="d-flex align-items-center me-1">
                    <Link to={link} title={iconTitle} aria-label={iconTitle} className="d-flex align-items-center">
                      <span className={`${icon} size-24 text-cool-gray-30`} aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </Col>
        <Col xs={12} md={8}>
          <div className="ps-md-2 ps-lg-3_5 ms-lg-3_5">
            <TabsPanel
              options={displayedTabs}
              selectedOption={selectedTab}
              onTabClick={setSelectedTab}
              className="d-flex mb-1_75 pt-2 sticky-content"
              tabClassName="px-0 pb-0_75 me-2"
            />

            {displayedTabs.map(({ ariaControls, value }) => (
              <div
                className={classnames('tab-section', { show: selectedTab === value })}
                role="tabpanel"
                id={ariaControls}
                aria-labelledby={value}
                aria-hidden={selectedTab !== value}
                key={ariaControls}
              >
                {value === TAB_NAMES.profile && (
                  <ContentFragment componentToUse="div" classes="size-16 text-cool-gray-10">
                    {authorBio}
                  </ContentFragment>
                )}
                {value === TAB_NAMES.articles && totalArticlesPages > 0 && (
                  <>
                    <ul className="list-unstyled mb-0">
                      {times(loadedArticlePagesNum, ind => ind + 1).map(pageNum => (
                        <AuthorArticlesPage
                          key={pageNum}
                          author={author}
                          pageNum={pageNum}
                          openVideoModal={onToggleVideoModal}
                        />
                      ))}
                    </ul>
                    {loadedArticlePagesNum < totalArticlesPages && (
                      <ShowMoreBtn onClick={loadNextArticlePage} ariaLabel="Show more articles" />
                    )}
                  </>
                )}
                {value === TAB_NAMES.videos && totalVideosPages > 0 && (
                  <>
                    <ul className="list-unstyled mb-0">
                      {times(loadedVideoPagesNum, ind => ind + 1).map(pageNum => (
                        <AuthorVideosPage
                          key={pageNum}
                          author={author}
                          pageNum={pageNum}
                          openVideoModal={onToggleVideoModal}
                        />
                      ))}
                    </ul>
                    {loadedVideoPagesNum < totalVideosPages && (
                      <ShowMoreBtn onClick={loadNextVideoPage} ariaLabel="Show more videos" />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>
      <YoutubeVideoModalAsync
        onToggleModal={onToggleVideoModal}
        isOpen={isVideoModalOpen}
        videoId={videoModalData.videoId}
        videoAspectRatio={videoModalData.videoAspectRatio}
        creativeId={videoModalData.creativeId}
        videoTitle={videoModalData.videoTitle}
      />
    </Container>
  );
}

AuthorUI.propTypes = {
  author: PropTypes.string.isRequired,
  content: CmsEntities.Content,
  articlesData: EditorialEntities.ArticlesData,
  videosData: EditorialEntities.VideosData,
};

export const Author = connectToModel(AuthorUI, {
  content: bindToContent(({ author }) => getAuthorContentPath(author)),
  articlesData: bindToPath(({ author }) => buildAuthorArticlesPath(author, 1, ARTICLE_PAGE_SIZE), EditorialModel),
  videosData: bindToPath(({ author }) => buildAuthorVideosPath(author, 1, ARTICLE_PAGE_SIZE), EditorialModel),
});
